import { useEffect, useRef } from 'react';
import api from '../lib/api';
import { haversineDistanceMeters } from '../utils/geo';

interface AutoCheckoutOptions {
  visitId: string | null;
  isActive: boolean;
  storeLat: number | null;
  storeLng: number | null;
  radiusMeters: number;
  /** Minutes outside geofence before auto-checkout (default: 5) */
  graceMinutes?: number;
  /** GPS poll interval in ms (default: 60000 = 1 min) */
  pollInterval?: number;
  /** Called before the checkout API is invoked */
  onBeforeCheckout?: () => void;
  onAutoCheckout?: () => void;
}

/**
 * Monitors promoter position during an active visit.
 * If the promoter leaves the store geofence for more than `graceMinutes`,
 * automatically triggers check-out.
 */
export function useAutoCheckout({
  visitId,
  isActive,
  storeLat,
  storeLng,
  radiusMeters,
  graceMinutes = 5,
  pollInterval = 60000,
  onBeforeCheckout,
  onAutoCheckout,
}: AutoCheckoutOptions) {
  const outsideSinceRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isActive || !visitId || storeLat == null || storeLng == null) {
      outsideSinceRef.current = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const check = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const dist = haversineDistanceMeters(
            pos.coords.latitude,
            pos.coords.longitude,
            storeLat,
            storeLng
          );

          if (dist <= radiusMeters * 1.5) {
            // Inside (with 50% tolerance) — reset timer
            outsideSinceRef.current = null;
            return;
          }

          // Outside geofence
          if (!outsideSinceRef.current) {
            outsideSinceRef.current = Date.now();
            return;
          }

          const minutesOutside = (Date.now() - outsideSinceRef.current) / 60000;
          if (minutesOutside >= graceMinutes) {
            // Auto-checkout
            try {
              onBeforeCheckout?.();
              await api.post(`/visits/${visitId}/check-out`, {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                notes: `Auto-checkout: promotor saiu do raio há ${Math.round(minutesOutside)} min`,
              });
              outsideSinceRef.current = null;
              onAutoCheckout?.();
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
            } catch (err) {
              console.error('Auto-checkout failed:', err);
            }
          }
        },
        () => {
          // GPS error — don't auto-checkout on GPS failure
        },
        { enableHighAccuracy: false, timeout: 10000 }
      );
    };

    // Start polling
    check();
    intervalRef.current = setInterval(check, pollInterval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, visitId, storeLat, storeLng, radiusMeters, graceMinutes, pollInterval, onBeforeCheckout, onAutoCheckout]);
}
